import {
  AvatarGroup,
  Box,
  Button,
  Divider,
  Flex,
  FlexProps,
  Table,
  TableCellProps,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { DeFiRow, PageData } from "./type";
import DefiRowTags from "./DefiRowTags";
import usePageData from "@hooks/usePageData";
import { Fragment, useMemo, useState } from "react";
import { DefiImg } from "@components/StorageImage";
import { trackEvent } from "utils";
import { HiArrowDown, HiArrowUp, HiOutlineArrowsUpDown } from "react-icons/hi2";
import {
  DEFI_ADD_LIQUIDITY,
  DEFI_SWAP,
  DEFI_WITHDRAW,
  FEATURED_OPPORTUNITIES,
  GENERAL_OPPORTUNITIES,
  VAULT_DEFI_ADD_LIQUIDITY,
} from "@constants/analytics";
import BaseImage from "@components/BaseImage";

interface TdProps extends TableCellProps {}

const TableCell = ({ children, ...props }: TdProps) => (
  <Td py={6} {...props}>
    {children}
  </Td>
);

interface ItemProps {
  row: DeFiRow;
  featuredDefi?: boolean;
}

const RowButtons = ({ row, featuredDefi = false }: ItemProps) => {
  const showWithdraw = row.Provider?.toLowerCase() == "olive";
  const oliveWithdrawUrl = "https://www.olivenetwork.xyz/stake";
  return (
    <Flex gap={2}>
      {!showWithdraw && row["Add Liquidity"] && (
        <Button
          as="a"
          href={row["Add Liquidity"]}
          target="_blank"
          size="sm"
          color="white"
          bg="#075A5A"
          _hover={{ bg: "#075A5A95" }}
          textTransform="uppercase"
          fontSize={11}
          px={{ base: 6, md: 3 }}
          flex={{
            base: 1,
            md: "none",
          }}
          onClick={() =>
            trackEvent(DEFI_ADD_LIQUIDITY, {
              defi_chain: row.Section,
              activity_type: row.Activity,
              defi_protocol: row.Provider,
              asset: row.Pair,
              source: featuredDefi
                ? FEATURED_OPPORTUNITIES
                : GENERAL_OPPORTUNITIES,
            })
          }
        >
          Add Liquidity
        </Button>
      )}

      {!showWithdraw && row.Swap && (
        <Button
          as="a"
          href={row.Swap}
          target="_blank"
          size="sm"
          bg="white"
          borderWidth={1}
          borderColor={"#B6DFDF"}
          textTransform="uppercase"
          fontSize={16}
          fontWeight={400}
          px={{ base: 0, md: 1 }}
          flex={{
            base: 1,
            md: "none",
          }}
          onClick={() =>
            trackEvent(DEFI_SWAP, {
              defi_chain: row.Section,
              activity_type: row.Activity,
              defi_protocol: row.Provider,
              asset: row.Pair,
              source: featuredDefi
                ? FEATURED_OPPORTUNITIES
                : GENERAL_OPPORTUNITIES,
            })
          }
        >
          <BaseImage
            src={"/assets/swap.svg"}
            alt="swap"
            width={24}
            height={24}
          />
        </Button>
      )}

      {/* KT77 - Olive Protocol Shut down  */}
      {showWithdraw && (
        <Button
          as="a"
          href={oliveWithdrawUrl}
          target="_blank"
          size="sm"
          bg="white"
          borderWidth={1}
          borderColor={"#B6DFDF"}
          textTransform="uppercase"
          fontSize={11}
          px={6}
          flex={{
            base: 1,
            md: "none",
          }}
          onClick={() =>
            trackEvent(DEFI_WITHDRAW, {
              chain: row.Section,
              activity_type: row.Activity,
              defi_protocol: row.Provider,
              asset: row.Pair,
              source: featuredDefi
                ? FEATURED_OPPORTUNITIES
                : GENERAL_OPPORTUNITIES,
            })
          }
        >
          Withdraw
        </Button>
      )}
    </Flex>
  );
};

const DefiItemLG = ({ row, ...props }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <Tr {...props}>
      <TableCell paddingLeft={0} style={{ textWrap: "nowrap" }} w="15%">
        <Flex align="flex-start" gap={2} alignItems={"center"}>
          <AvatarGroup size="sm" max={2} spacing="-0.4rem">
            <DefiImg img={row["Token 1 Icon"]} />
            <DefiImg img={row["Token 2 Icon"]} />
          </AvatarGroup>
          <Flex direction="column" gap={2}>
            <Flex>{row.Pair || "-"}</Flex>
          </Flex>
        </Flex>
      </TableCell>
      <TableCell w="14%">
        <Flex align="center" gap={2}>
          <DefiImg img={row.Provider} size="sm" />
          <div>{row.Provider || "-"}</div>
        </Flex>
      </TableCell>
      <TableCell fontWeight="bold" w="14%">
        {pageData?.allTags && (row.Tags || row.TagWithImage) ? (
          <DefiRowTags
            allTags={pageData.allTags.data}
            tags={row.Tags}
            tagWithImage={row.TagWithImage}
            tagImagePath={row.TagImagePath}
          />
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell fontWeight="bold" w="13%">
        <div>{row.Activity}</div>
      </TableCell>
      <TableCell fontWeight="bold" w="4%">
        <div>{row.APY || "-"}</div>
      </TableCell>
      <TableCell fontWeight="bold" w="4%">
        {row.TVL || "-"}
      </TableCell>
      <TableCell w="2%" p={{ base: 0, md: "8px" }}>
        <RowButtons row={row} />
      </TableCell>
    </Tr>
  );
};

const DefiItemSM = ({ row }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <Flex w="100%" direction="column" gap={6}>
      <Flex direction="column" gap={2}>
        <Flex align="center" gap={2} justify="space-between">
          <Flex gap={2} align="center">
            <AvatarGroup size="sm" max={2} spacing="-0.4rem">
              <DefiImg img={row["Token 1 Icon"]} />
              <DefiImg img={row["Token 2 Icon"]} />
            </AvatarGroup>
            <Flex direction="column">
              <Flex>{row.Pair}</Flex>
            </Flex>
          </Flex>
          <Box>
            <DefiImg img={row.Provider} size="sm" />
          </Box>
        </Flex>

        <Flex direction="column" gap={2}>
          {pageData?.allTags && row.Tags ? (
            <DefiRowTags
              allTags={pageData.allTags.data}
              tags={row.Tags}
              tagWithImage={row.TagWithImage}
              tagImagePath={row.TagImagePath}
            />
          ) : null}
        </Flex>
      </Flex>

      <Flex
        align="center"
        justify="space-between"
        fontSize={"12px"}
        fontWeight={600}
      >
        {/* <Box>APY: {row.APY}</Box> */}
        <Box>TVL: {row.TVL || "-"}</Box>

        <RowButtons row={row} />
      </Flex>
    </Flex>
  );
};

interface BodyProps {
  rows: DeFiRow[];
}

const DefiBodyMD = ({ rows }: BodyProps) => {
  return (
    <Tbody>
      {rows.map((row, i) => (
        <DefiItemLG row={row} key={`${row.Pair}_${i}`} />
      ))}
    </Tbody>
  );
};

const DefiBodySM = ({ rows, ...props }: BodyProps & FlexProps) => {
  return (
    <Flex
      gap={6}
      direction="column"
      {...props}
      p={8}
      bg={"backgroundCard"}
      // borderRadius={"8px"}
    >
      {rows.map((row, i) => (
        <Fragment key={`${row.Pair}_${i}`}>
          {i > 0 ? <Divider /> : null}
          <DefiItemSM row={row} key={row.Pair} />
        </Fragment>
      ))}
    </Flex>
  );
};

interface Props {
  rows: DeFiRow[];
}

const DefiList = ({ rows }: Props) => {
  const [sortConfig, setSortConfig] = useState<{ key: any; direction: any }>({
    key: "APY",
    direction: "descending",
  });

  const sortedData = useMemo(() => {
    if (sortConfig.key) {
      return [...rows].sort((a, b) => {
        let v1: any = a[sortConfig.key];
        let v2: any = b[sortConfig.key];
        if (
          sortConfig.key == "APY" &&
          !["", "-"].includes(v1) &&
          !["", "-"].includes(v2)
        ) {
          v1 = parseFloat(v1) / 100.0;
          v2 = parseFloat(v2) / 100.0;
        }
        if (sortConfig.key == "TVLinDigits") {
          if (["", "-"].includes(v1)) v1 = "0";
          if (["", "-"].includes(v2)) v2 = "0";
          v1 = parseFloat(v1.replace(/[$,]+/g, ""));
          v2 = parseFloat(v2.replace(/[$,]+/g, ""));
        }
        if (v1 < v2) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (v1 > v2) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return rows;
  }, [rows, sortConfig]);

  const requestSort = (key: string) => {
    let direction: any = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      setSortConfig({ key: null, direction: null });
      return;
    }
    setSortConfig({ key, direction });
  };
  const getIcon = (column: string) => {
    if (sortConfig.key !== column) return <HiOutlineArrowsUpDown />;
    return sortConfig.direction === "ascending" ? (
      <HiArrowUp />
    ) : (
      <HiArrowDown />
    );
  };

  return (
    <>
      <Table
        border="hidden"
        borderRadius="24px"
        hideBelow="md"
        size="sm"
        variant="unstyled"
        bg="backgroundCard"
      >
        <Box p={{ base: "20px", md: "30px" }}>
          {/* <DefiListHeader /> */}
          <Thead border="hidden">
            <Tr>
              <Td paddingLeft={0}>Assets</Td>
              <Td>Provider</Td>
              <Td>Benefit</Td>
              <Td>Activity</Td>
              <Td onClick={() => requestSort("APY")} cursor="pointer">
                <Flex gap={2}>APY {getIcon("APY")}</Flex>
              </Td>
              <Td onClick={() => requestSort("TVLinDigits")} cursor="pointer">
                <Flex gap={2}>TVL {getIcon("TVLinDigits")}</Flex>
              </Td>
              <Td></Td>
            </Tr>
          </Thead>
          <DefiBodyMD rows={sortedData} />
        </Box>
      </Table>
      <DefiBodySM rows={rows} hideFrom="md" />
    </>
  );
};

export const DefiFeaturedItem = ({ row }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <Flex
      w="100%"
      direction="column"
      gap={6}
      bgColor={"backgroundCard"}
      border="hidden"
      borderRadius="24px"
      p={5}
      minW="300px"
    >
      <Flex direction="column" gap={2}>
        <Flex align="center" gap={2} justify="space-between">
          <Flex gap={2} align="center">
            <AvatarGroup size="sm" max={2} spacing="-0.4rem">
              <DefiImg img={row["Token 1 Icon"]} />
              <DefiImg img={row["Token 2 Icon"]} />
            </AvatarGroup>
            <Flex direction="column">
              <Flex>{row.Pair}</Flex>
            </Flex>
          </Flex>
          <Box>
            <DefiImg img={row.Provider} size="sm" />
          </Box>
        </Flex>
      </Flex>

      <Flex
        align="center"
        justify="space-between"
        fontSize={"12px"}
        fontWeight={600}
      >
        <Flex direction={"column"}>
          <Text fontSize={"20px"} fontWeight={700}>
            {row.TVL || "-"}
          </Text>
          <Flex alignItems={"center"} gap={1}>
            <Text fontSize={"12px"} fontWeight={500}>
              TVL
            </Text>
          </Flex>
        </Flex>
        <Flex direction={"column"}>
          <Text fontSize={"20px"} fontWeight={700}>
            {row.APY || "-"}
          </Text>
          <Flex alignItems={"center"} gap={1}>
            <Text fontSize={"12px"} fontWeight={500}>
              APY
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex direction="column" gap={2}>
        {pageData?.allTags &&
          (row.Activity || row.TagWithImage || row.Tags) && (
            <DefiRowTags
              allTags={pageData.allTags.data}
              tags={[row.Activity, row.Tags, row.TagWithImage].join(",")}
              tagWithImage={row.TagWithImage}
              tagImagePath={row.TagImagePath}
              featuredDefi
            />
          )}
      </Flex>

      <RowButtons row={row} featuredDefi />
    </Flex>
  );
};

const getPairText = (input: string) => {
  if (input.includes(" - ")) {
    const [part1, part2] = input.split(" - ").map((str) => str.trim());
    return part1 + "- " + part2;
  } else {
    return input.trim(); // "agETH" (or whatever the single word is)
  }
};

export const DefiVaultPartnerships = ({ row }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <Flex
      w="100%"
      direction="column"
      gap={1}
      bgColor={"backgroundCanvas"}
      border="hidden"
      borderRadius="24px"
      p="1rem"
      minW="200px"
      height={"176px"}
      justifyContent={"space-between"}
    >
      <Flex direction="column" gap={1}>
        <Flex flexDirection="column" gap={1} justifyContent="space-between">
          <Flex gap={1} alignItems={"center"}>
            <DefiImg img={row.Provider} size="xs" />
            <Text fontSize={"14px"}>{row.Provider}</Text>
          </Flex>
          <Flex gap={"2px"} align="center" justifyContent={"flex-end"}>
            <AvatarGroup size="xs" max={2} spacing="-0.4rem">
              <DefiImg img={row["Token 1 Icon"]} />
              <DefiImg img={row["Token 2 Icon"]} />
            </AvatarGroup>
            <Flex direction="column">
              <Text fontSize={"14px"}>{getPairText(row.Pair)}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Flex direction={"column"}>
        <Flex direction="column" fontSize={"12px"} fontWeight={600}>
          <Flex justifyContent="space-between">
            <Text fontWeight={300}>TVL</Text>

            <Text>{row.TVL || "-"}</Text>
          </Flex>
          <Flex justifyContent="space-between">
            <Text fontWeight={300}>APY</Text>

            <Text>{row.APY || "-"}</Text>
          </Flex>
        </Flex>

        <Flex direction="column" gap={2}>
          {pageData?.allTags &&
            (row.Activity || row.TagWithImage || row.Tags) && (
              <DefiRowTags
                allTags={pageData.allTags.data}
                tags={[row.Activity, row.Tags, row.TagWithImage].join(",")}
                tagWithImage={row.TagWithImage}
                tagImagePath={row.TagImagePath}
                featuredDefi
              />
            )}
        </Flex>

        {row["Add Liquidity"] && (
          <Button
            mt={"16px"}
            as="a"
            href={row["Add Liquidity"]}
            target="_blank"
            size="sm"
            color="#FFFFFF"
            bg="#3F6870"
            border={"1px solid #B6DFDF"}
            _hover={{ bg: "#3F6870" }}
            textTransform="uppercase"
            fontSize={11}
            px={6}
            flex={{
              // base: 1,
              md: "none",
            }}
            onClick={() =>
              trackEvent(VAULT_DEFI_ADD_LIQUIDITY, {
                chain: row.Section,
                activity_type: row.Activity,
                defi_protocol: row.Provider,
                asset: row.Pair,
              })
            }
          >
            Deposit
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default DefiList;
